import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, QrCode, Loader2, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PagarmePixDialog — automated Pagar.me PIX charge.
 *
 * Props:
 *   open, onOpenChange
 *   flow: "deposit" | "checkout"
 *   amount: number (BRL)
 *   purpose?: string  (checkout only — ads/plan/service/...)
 *   description?: string
 *   referenceId?: string
 *   onPaid?: (order) => void   fired once the order flips to "paid"
 *
 * The dialog is mounted permanently by the parent (React 18 portal-safe).
 * When it opens with a fresh amount, it POSTs to create the PIX charge, shows
 * the QR + copia-e-cola, and polls status every 4s until "paid".
 */
export default function PagarmePixDialog({
  open,
  onOpenChange,
  flow = "deposit",
  amount = 0,
  purpose = "wallet_deposit",
  description = "",
  referenceId = null,
  onPaid,
}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState("pending");
  const pollRef = useRef(null);
  const paidFiredRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setOrder(null); setError(""); setStatus("pending"); setCopied(false);
      paidFiredRef.current = false;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (order) return;
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const path = flow === "checkout" ? "/pagarme/pix/checkout" : "/pagarme/pix/deposit";
        const body = flow === "checkout"
          ? { amount, purpose, description, reference_id: referenceId }
          : { amount };
        const r = await axios.post(`${API}${path}`, body);
        if (!alive) return;
        setOrder(r.data);
        setStatus(r.data.status || "pending");
      } catch (err) {
        if (!alive) return;
        const detail = err.response?.data?.detail;
        if (detail === "payments_not_configured") {
          setError("O gateway Pagar.me ainda não foi configurado. Peça ao administrador para adicionar as chaves de API.");
        } else {
          setError(detail || err.message || "Erro ao criar cobrança PIX");
        }
      }
      setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll status every 4s while the dialog is open and status is pending
  useEffect(() => {
    if (!open || !order?.internal_code || status !== "pending") return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/pagarme/pix/${order.internal_code}`);
        const s = r.data.status || "pending";
        setStatus(s);
        if (s === "paid" && !paidFiredRef.current) {
          paidFiredRef.current = true;
          onPaid?.(r.data);
          toast.success("Pagamento confirmado!");
        }
        if (s !== "pending" && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } catch { /* silent — will retry */ }
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [open, order, status, onPaid]);

  const copyEmv = async () => {
    if (!order?.emv) return;
    try {
      await navigator.clipboard.writeText(order.emv);
      setCopied(true);
      toast.success("Código Pix copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info(order.emv);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pagarme-pix-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            Pagar com Pix
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Gerando cobrança...
          </div>
        )}

        {error && (
          <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 flex items-start gap-2" data-testid="pagarme-pix-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          </div>
        )}

        {order && status === "paid" && (
          <div className="p-6 rounded-lg border border-emerald-500/40 bg-emerald-500/10 flex flex-col items-center gap-2" data-testid="pagarme-pix-paid">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">Pagamento confirmado!</div>
            <div className="text-sm text-muted-foreground">R$ {Number(order.amount_brl).toFixed(2)} creditados</div>
            <Button className="mt-3 rounded-full" onClick={() => onOpenChange(false)} data-testid="pagarme-pix-close">
              Fechar
            </Button>
          </div>
        )}

        {order && status === "pending" && (
          <div className="space-y-4" data-testid="pagarme-pix-pending">
            <div className="p-4 rounded-lg bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/40 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Valor</div>
                <div className="text-3xl font-bold" data-testid="pagarme-pix-amount">R$ {Number(order.amount_brl).toFixed(2)}</div>
              </div>
              {order.qr_code_url && (
                <div className="bg-white rounded-lg p-3 flex items-center justify-center">
                  <img src={order.qr_code_url} alt="QR Code PIX" className="w-56 h-56 object-contain" data-testid="pagarme-pix-qr" />
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Pix copia e cola</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="font-mono text-[11px] break-all flex-1 bg-muted/40 p-2 rounded" data-testid="pagarme-pix-emv">
                    {order.emv || "—"}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyEmv} disabled={!order.emv}
                    data-testid="pagarme-pix-copy" className="rounded-full flex-shrink-0">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                Aguardando pagamento — a confirmação chega automaticamente
              </div>
            </div>

            <ol className="text-xs text-muted-foreground space-y-1 pl-4 list-decimal">
              <li>Abra o app do seu banco</li>
              <li>Escaneie o QR ou cole o código Pix</li>
              <li>Confira o valor e confirme</li>
              <li>O crédito aparece automaticamente aqui</li>
            </ol>

            <button
              onClick={() => onOpenChange(false)}
              className="w-full text-xs text-muted-foreground hover:text-foreground py-1"
              data-testid="pagarme-pix-cancel"
            >
              Fechar (o pagamento continua válido)
            </button>
          </div>
        )}

        {order && (status === "canceled" || status === "failed") && (
          <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-sm" data-testid="pagarme-pix-failed">
            Esta cobrança foi cancelada ou expirou. Feche e gere uma nova.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
